// services/dashboard/formatDataIndicators.js

// ==================== CONSTANTES ====================
const COLOR_PALETTE = [
  "rgba(9, 127, 159, 0.7)",
  "rgba(255, 205, 40, 0.7)",
  "rgba(255, 99, 132, 0.7)",
  "rgba(54, 162, 235, 0.7)",
  "rgba(255, 159, 64, 0.7)",
  "rgba(153, 102, 255, 0.7)",
  "rgba(75, 192, 192, 0.7)",
  "rgba(255, 99, 71, 0.7)",
  "rgba(106, 90, 205, 0.7)",
  "rgba(34, 193, 195, 0.7)"
];

// ==================== FUNCIONES AUXILIARES ====================

/**
 * Convierte un color hexadecimal a rgba con opacidad
 */
const hexToRgba = (hex, opacity = 0.7) => {
  hex = hex.replace('#', '');
  
  if (hex.length === 3) {
    hex = hex.split('').map(char => char + char).join('');
  }
  
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/**
 * Normaliza el color del indicador
 */
const normalizeColor = (color, opacity = 0.7) => {
  if (!color) return undefined;
  
  if (color.startsWith('rgba') || color.startsWith('rgb')) {
    return color;
  }
  
  if (color.startsWith('#')) {
    return hexToRgba(color, opacity);
  }
  
  return color;
};

/**
 * Extrae el valor numérico de un string
 */
const extractNumericValue = (result) => {
  if (typeof result === 'number') return result;
  const match = result.match(/-?\d+\.?\d*/);
  return match ? parseFloat(match[0]) : 0;
};

/**
 * Extrae el valor numérico y la unidad de un string
 */
const extractValueAndUnit = (result) => {
  if (typeof result === 'number') return { value: result };
  
  const str = String(result).trim();
  const match = str.match(/^(-?\d+\.?\d*)\s*(.+)?$/);
  
  if (match) {
    const value = parseFloat(match[1]);
    const unitPart = match[2]?.trim();
    const unit = unitPart && unitPart.length > 0 ? unitPart : undefined;
    return { value, unit };
  }
  
  return { value: 0 };
};

/**
 * Formatea una fecha de "2025-08-01" a "Ago 2025"
 */
const formatDate = (dateStr) => {
  const months = [
    'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
    'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'
  ];
  const [year, month] = dateStr.split('-');
  const monthIndex = parseInt(month) - 1;
  return `${months[monthIndex]} ${year}`;
};

/**
 * Limpia el nombre del indicador
 */
const cleanIndicatorName = (indicatorKey) => {
  const indicatorName = indicatorKey.split('__')[1] || indicatorKey;
  return indicatorName
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .replace(/variacion porcentual/gi, 'Var. %')
    .replace(/variacion mensual/gi, 'Var. Mensual')
    .trim();
};

// ==================== FUNCIONES PRINCIPALES ====================

/**
 * Procesa los datos raw de indicadores y los agrupa por fecha
 */
export const processIndicatorsData = (rawData) => {
  const indicatorsByDate = {};

  if (!rawData || !rawData.data || typeof rawData.data !== 'object') {
    console.warn('processIndicatorsData: Datos inválidos recibidos');
    return {};
  }

  Object.entries(rawData.data).forEach(([date, companies]) => {
    if (!companies || typeof companies !== 'object') return;

    indicatorsByDate[date] = {};

    Object.entries(companies).forEach(([companyKey, companyData]) => {
      if (!companyData || typeof companyData !== 'object') return;

      Object.entries(companyData).forEach(([groupKey, groupData]) => {
        if (
          typeof groupData === 'object' &&
          groupData !== null &&
          groupKey.startsWith('grupo_')
        ) {
          Object.entries(groupData).forEach(([indicatorKey, indicatorData]) => {
            if (
              indicatorKey.includes('__') &&
              indicatorData &&
              typeof indicatorData === 'object'
            ) {
              // Procesar el indicador normal con result
              if (indicatorData.result) {
                const { value, unit: extractedUnit } = extractValueAndUnit(indicatorData.result);
                const cleanName = cleanIndicatorName(indicatorKey);
                const unit = extractedUnit || indicatorData.indicator_unit;
                const color = normalizeColor(indicatorData.indicator_color);

                if (indicatorsByDate[date][cleanName]) {
                  indicatorsByDate[date][cleanName].value += value;
                  if (!indicatorsByDate[date][cleanName].color && color) {
                    indicatorsByDate[date][cleanName].color = color;
                  }
                } else {
                  indicatorsByDate[date][cleanName] = { value, unit, color };
                }
              }

              // Procesar el indicador con absolute si existe
              if (indicatorData.absolute && typeof indicatorData.absolute === 'object') {
                const absoluteData = indicatorData.absolute;
                
                if (absoluteData.name && absoluteData.value !== undefined && absoluteData.value !== '') {
                  const { value: absoluteValue, unit: extractedUnit } = extractValueAndUnit(absoluteData.value);
                  const absoluteName = absoluteData.name;
                  const unit = extractedUnit || indicatorData.indicator_unit;
                  const color = normalizeColor(indicatorData.indicator_color);

                  if (indicatorsByDate[date][absoluteName]) {
                    indicatorsByDate[date][absoluteName].value += absoluteValue;
                    if (!indicatorsByDate[date][absoluteName].color && color) {
                      indicatorsByDate[date][absoluteName].color = color;
                    }
                  } else {
                    indicatorsByDate[date][absoluteName] = { value: absoluteValue, unit, color };
                  }
                }
              }
            }
          });
        }
      });
    });
  });

  return indicatorsByDate;
};

/**
 * Convierte los datos procesados al formato que Chart.js necesita
 */
export const convertToChartJsFormat = (processedData) => {
  const allIndicators = new Set();
  Object.values(processedData).forEach((dateData) => {
    Object.keys(dateData).forEach((indicator) => allIndicators.add(indicator));
  });

  const sortedDates = Object.keys(processedData).sort();

  const datasets = Array.from(allIndicators).map((indicator, index) => {
    let unit;
    let color;
    
    for (const date of sortedDates) {
      if (processedData[date][indicator]) {
        if (!unit && processedData[date][indicator].unit) {
          unit = processedData[date][indicator].unit;
        }
        if (!color && processedData[date][indicator].color) {
          color = processedData[date][indicator].color;
        }
        if (unit && color) break;
      }
    }

    const backgroundColor = color || COLOR_PALETTE[index % COLOR_PALETTE.length];
    const borderColor = color 
      ? color.replace('0.7', '1') 
      : COLOR_PALETTE[index % COLOR_PALETTE.length].replace('0.7', '1');

    return {
      label: indicator,
      data: sortedDates.map((date) => processedData[date][indicator]?.value || 0),
      backgroundColor,
      borderColor,
      borderWidth: 1,
      unit,
    };
  });

  return {
    labels: sortedDates.map(formatDate),
    datasets,
  };
};

/**
 * Función principal para preparar los datos del gráfico de barras
 */
export const prepareBarChartData = (rawData) => {
  try {
    if (!rawData || !rawData.data || typeof rawData.data !== 'object') {
      console.warn('prepareBarChartData: Datos inválidos recibidos');
      return null;
    }

    if (Object.keys(rawData.data).length === 0) {
      return null;
    }

    const processed = processIndicatorsData(rawData);

    if (Object.keys(processed).length === 0) {
      return null;
    }

    const chartData = convertToChartJsFormat(processed);
    return chartData;
  } catch (error) {
    console.error('Error en prepareBarChartData:', error);
    return null;
  }
};